import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markAsRead, markAllAsRead } from '../api/feedApi';
import { formatDate } from '../utils/dateFormatter';
import './Notifications.css';

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, unread, read

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getNotifications({ limit: 100 });
      if (response.success) {
        setNotifications(response.data.notifications);
      }
    } catch (err) {
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      try {
        await markAsRead(notification.id);
        setNotifications(notifications.map(n => 
          n.id === notification.id ? { ...n, is_read: true } : n
        ));
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }

    if (notification.link) {
      navigate(notification.link);
      return;
    }
    if (notification.type?.startsWith('job_')) {
      navigate('/post-job/status');
      return;
    }
    if (notification.post_id) {
      navigate(`/feed/${notification.post_id}`);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      alert(err.message || 'Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like': return '👍';
      case 'dislike': return '👎';
      case 'comment': return '💬';
      case 'job_pending': return '⏳';
      case 'job_approved': return '✅';
      case 'job_rejected': return '📝';
      default: return '🔔';
    }
  };

  const getNotificationText = (notification) => {
    if (notification.type?.startsWith('job_')) {
      return (
        <>
          <strong>{notification.title || 'Job update'}</strong>
          {notification.message ? (
            <>
              <br />
              <span style={{ whiteSpace: 'pre-wrap' }}>{notification.message}</span>
            </>
          ) : null}
        </>
      );
    }
    const username = notification.triggered_by_username;
    switch (notification.type) {
      case 'like':
        return (
          <>
            <strong>{username}</strong> liked your post
          </>
        );
      case 'dislike':
        return (
          <>
            <strong>{username}</strong> disliked your post
          </>
        );
      case 'comment':
        return (
          <>
            <strong>{username}</strong> commented on your post
          </>
        );
      default:
        return notification.message || notification.title || 'New notification';
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="notifications-page">
      <div className="notifications-container">
        <div className="notifications-header">
          <h1>Notifications</h1>
          {unreadCount > 0 && (
            <button 
              className="mark-all-read-btn"
              onClick={handleMarkAllAsRead}
            >
              Mark all as read
            </button>
          )}
        </div>

        <div className="notifications-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({notifications.length})
          </button>
          <button
            className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread ({unreadCount})
          </button>
          <button
            className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
            onClick={() => setFilter('read')}
          >
            Read ({notifications.length - unreadCount})
          </button>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchNotifications}>Try Again</button>
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🔔</span>
            <h3>No notifications</h3>
            <p>
              {filter === 'unread' 
                ? "You're all caught up!" 
                : filter === 'read'
                ? "No read notifications"
                : "You don't have any notifications yet"}
            </p>
          </div>
        ) : (
          <div className="notifications-list">
            {filteredNotifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-card ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-icon-large">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-details">
                  <p className="notification-message">
                    {getNotificationText(notification)}
                  </p>
                  {notification.post_title && (
                    <p className="notification-post">
                      Post: <span>"{notification.post_title}"</span>
                    </p>
                  )}
                  <span className="notification-timestamp">
                    {formatDate(notification.created_at)}
                  </span>
                </div>
                {!notification.is_read && (
                  <span className="unread-indicator"></span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
